'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import { Paperclip, X, Image, FileText, Download, Upload, Loader2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getAuthHeaders } from '@/lib/api-utils'
import { toast } from '@/hooks/use-toast'

export interface Attachment {
  id: string
  filename: string
  original_name: string
  content_type: string
  filesize: number
  url: string
  uploaded_by_name?: string | null
  created_at: string
}

// ─── Pending file (before issue exists) ──────────────────────────────────────

export interface PendingFile {
  file: File
  previewUrl?: string
}

interface PendingAttachmentsProps {
  files: PendingFile[]
  onChange: (files: PendingFile[]) => void
}

export function PendingAttachments({ files, onChange }: PendingAttachmentsProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || [])
    const newFiles: PendingFile[] = selected.map(f => ({
      file: f,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
    }))
    onChange([...files, ...newFiles])
    e.target.value = ''
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const dropped = Array.from(e.dataTransfer.files)
    const newFiles: PendingFile[] = dropped.map(f => ({
      file: f,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
    }))
    onChange([...files, ...newFiles])
  }, [files, onChange])

  const remove = (idx: number) => {
    const next = [...files]
    if (next[idx].previewUrl) URL.revokeObjectURL(next[idx].previewUrl!)
    next.splice(idx, 1)
    onChange(next)
  }

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[var(--of-border)] rounded-lg p-5 cursor-pointer hover:border-[var(--of-violet)] hover:bg-[var(--of-primary-10)] transition-colors"
      >
        <Upload className="h-5 w-5 text-[var(--of-fg-3)]" />
        <p className="text-sm text-[var(--of-fg-2)]">
          Drop screenshots here or <span className="text-[var(--of-violet)] font-medium">browse</span>
        </p>
        <p className="text-xs text-[var(--of-fg-3)]">PNG, JPG, GIF, PDF up to 10 MB each</p>
        <input ref={inputRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={handleAdd} />
      </div>

      {/* Preview list */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((pf, i) => (
            <div key={i} className="relative group">
              {pf.previewUrl ? (
                <div className="w-20 h-20 rounded-lg overflow-hidden border border-[var(--of-border)]">
                  <img src={pf.previewUrl} alt={pf.file.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-lg border border-[var(--of-border)] flex flex-col items-center justify-center gap-1 bg-[var(--of-bg-elev)]">
                  <FileText className="h-6 w-6 text-[var(--of-fg-3)]" />
                  <span className="text-[10px] text-[var(--of-fg-3)] text-center px-1 truncate w-full text-center">
                    {pf.file.name.split('.').pop()?.toUpperCase()}
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={e => { e.stopPropagation(); remove(i) }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[var(--of-coral)] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
              <p className="text-[10px] text-[var(--of-fg-3)] mt-1 w-20 truncate text-center">{pf.file.name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Saved attachments (on an existing issue) ─────────────────────────────────

interface IssueAttachmentsProps {
  issueId: number | string
  initialAttachments?: Attachment[]
}

export function IssueAttachments({ issueId, initialAttachments = [] }: IssueAttachmentsProps) {
  const [attachments, setAttachments] = useState<Attachment[]>(initialAttachments)
  const [uploading, setUploading] = useState(false)
  const [lightbox, setLightbox] = useState<Attachment | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // The parent fetches attachments in a useEffect after mount, so the initial
  // [] prop arrives before the real list. Without this sync the component would
  // stay stuck on the empty state even when the parent has data.
  useEffect(() => {
    setAttachments(initialAttachments)
  }, [initialAttachments])

  const upload = async (files: File[]) => {
    const authHeaders = getAuthHeaders() as Record<string, string>
    if (!authHeaders || files.length === 0) return
    // Multipart bodies need the browser-generated Content-Type (includes the
    // boundary); explicit application/json corrupts the request.
    const { 'Content-Type': _ct, ...uploadHeaders } = authHeaders
    setUploading(true)
    try {
      for (const file of files) {
        const fd = new FormData()
        fd.append('file', file)
        const res = await fetch(`/api/issues/${issueId}/attachments`, {
          method: 'POST',
          headers: uploadHeaders,
          body: fd,
        })
        if (res.ok) {
          const { attachment } = await res.json()
          setAttachments(prev => [...prev, attachment])
        } else {
          const body = await res.json().catch(() => ({}))
          const reason = body?.error || `HTTP ${res.status}`
          toast({
            title: 'Upload failed',
            description: `${file.name}: ${reason}${body?.stage ? ` (stage: ${body.stage})` : ''}`,
            variant: 'destructive',
          })
        }
      }
    } finally {
      setUploading(false)
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    upload(Array.from(e.target.files || []))
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    upload(Array.from(e.dataTransfer.files))
  }

  const handleDelete = async (att: Attachment) => {
    const authHeaders = getAuthHeaders()
    if (!authHeaders) return
    try {
      await fetch(`/api/issues/${issueId}/attachments`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders as Record<string, string> },
        body: JSON.stringify({ attachmentId: att.id }),
      })
      setAttachments(prev => prev.filter(a => a.id !== att.id))
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' })
    }
  }

  const isImage = (att: Attachment) => att.content_type.startsWith('image/')

  const fmtSize = (bytes: number) =>
    bytes < 1024 ? `${bytes} B`
    : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`

  return (
    <div className="space-y-4">
      {/* Upload strip */}
      <div
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className="flex items-center gap-3 border border-dashed border-[var(--of-border)] rounded-lg px-4 py-3 cursor-pointer hover:border-[var(--of-violet)] hover:bg-[var(--of-primary-10)] transition-colors"
      >
        {uploading
          ? <Loader2 className="h-4 w-4 animate-spin text-[var(--of-violet)]" />
          : <Upload className="h-4 w-4 text-[var(--of-fg-3)]" />
        }
        <span className="text-sm text-[var(--of-fg-2)]">
          {uploading ? 'Uploading…' : 'Drop files or click to attach screenshots'}
        </span>
        <input ref={inputRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={handleFileInput} />
      </div>

      {/* Grid of thumbnails */}
      {attachments.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {attachments.map(att => (
            <div key={att.id} className="group relative border border-[var(--of-border)] rounded-lg overflow-hidden bg-[var(--of-bg-elev)]">
              {isImage(att) ? (
                <button
                  type="button"
                  onClick={() => setLightbox(att)}
                  className="w-full aspect-video block"
                >
                  <img
                    src={att.url}
                    alt={att.original_name}
                    className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                  />
                </button>
              ) : (
                <a
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center aspect-video gap-2"
                >
                  <FileText className="h-8 w-8 text-[var(--of-fg-3)]" />
                  <span className="text-xs text-[var(--of-fg-3)] font-medium">
                    {att.original_name.split('.').pop()?.toUpperCase()}
                  </span>
                </a>
              )}

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <a
                  href={att.url}
                  download={att.original_name}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center hover:bg-white"
                >
                  <Download className="h-3.5 w-3.5 text-gray-800" />
                </a>
                <button
                  type="button"
                  onClick={() => handleDelete(att)}
                  className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center hover:bg-white"
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </button>
              </div>

              {/* File name */}
              <div className="px-2 py-1.5 border-t border-[var(--of-border)]">
                <p className="text-[11px] text-[var(--of-fg-2)] truncate">{att.original_name}</p>
                <p className="text-[10px] text-[var(--of-fg-3)]">{fmtSize(att.filesize)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {attachments.length === 0 && !uploading && (
        <p className="text-sm text-[var(--of-fg-3)]">No attachments yet.</p>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-4xl max-h-full" onClick={e => e.stopPropagation()}>
            <img
              src={lightbox.url}
              alt={lightbox.original_name}
              className="max-w-full max-h-[85vh] rounded-lg object-contain"
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <a
                href={lightbox.url}
                download={lightbox.original_name}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center"
              >
                <Download className="h-4 w-4 text-white" />
              </a>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center"
              >
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
            <p className="mt-2 text-center text-sm text-white/70">{lightbox.original_name}</p>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Helper: upload pending files after issue creation ────────────────────────

export interface PendingUploadFailure {
  name: string
  reason: string
  stage?: string
}

export interface PendingUploadResult {
  uploaded: number
  failed: number
  failedNames: string[]
  failures: PendingUploadFailure[]
}

export async function uploadPendingAttachments(
  issueId: number | string,
  files: PendingFile[],
): Promise<PendingUploadResult> {
  const authHeaders = getAuthHeaders() as Record<string, string>
  const result: PendingUploadResult = { uploaded: 0, failed: 0, failedNames: [], failures: [] }
  if (!authHeaders || files.length === 0) return result
  // Multipart bodies need browser-generated Content-Type with boundary;
  // remove any explicit application/json so the server can parse the file.
  const { 'Content-Type': _ct, ...uploadHeaders } = authHeaders
  for (const pf of files) {
    const fd = new FormData()
    fd.append('file', pf.file)
    try {
      const res = await fetch(`/api/issues/${issueId}/attachments`, {
        method: 'POST',
        headers: uploadHeaders,
        body: fd,
      })
      if (res.ok) {
        result.uploaded++
      } else {
        const body = await res.json().catch(() => ({} as any))
        result.failed++
        result.failedNames.push(pf.file.name)
        result.failures.push({
          name: pf.file.name,
          reason: body?.error || `HTTP ${res.status}`,
          stage: body?.stage,
        })
      }
    } catch (err: any) {
      result.failed++
      result.failedNames.push(pf.file.name)
      result.failures.push({ name: pf.file.name, reason: err?.message || 'Network error' })
    } finally {
      if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl)
    }
  }
  return result
}
